import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import SEOHead from '../../components/SEOHead';

export default function ContactPage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const baseUrl = import.meta.env.VITE_SITE_URL || 'https://jackspumps.ie';

  const contactSchema = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": `${baseUrl}/contact#webpage`,
      "url": `${baseUrl}/contact`,
      "name": "Contact Jack's Pumps | 24/7 Emergency Pump Repairs Ireland",
      "description": "Contact Jack's Pumps for emergency pump repairs, service enquiries, and quotes. 24/7 emergency response across Dublin, Wicklow, Wexford, Kildare, Meath, Louth, and Carlow. Call +353 404 54753 or submit an online enquiry.",
      "isPartOf": {"@id": `${baseUrl}/#website`},
      "about": {"@id": `${baseUrl}/#organization`}
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": `${baseUrl}/`
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Contact",
          "item": `${baseUrl}/contact`
        }
      ]
    },
    {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "mainEntity": {
        "@type": "LocalBusiness",
        "name": "Jack's Pumps",
        "url": baseUrl,
        "telephone": "+35340454753",
        "email": "info@jackspumps.ie",
        "address": {
          "@type": "PostalAddress",
          "addressRegion": "Ireland",
          "addressCountry": "IE"
        },
        "areaServed": [
          {
            "@type": "City",
            "name": "Dublin"
          },
          {
            "@type": "City",
            "name": "Wicklow"
          },
          {
            "@type": "City",
            "name": "Wexford"
          },
          {
            "@type": "City",
            "name": "Kildare"
          },
          {
            "@type": "City",
            "name": "Meath"
          },
          {
            "@type": "City",
            "name": "Louth"
          },
          {
            "@type": "City",
            "name": "Carlow"
          }
        ],
        "openingHoursSpecification": {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": [
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday",
            "Sunday"
          ],
          "opens": "00:00",
          "closes": "23:59"
        }
      }
    }
  ];

  return (
    <>
      <SEOHead
        title="Contact Jack's Pumps | 24/7 Emergency Pump Repairs Dublin & Leinster"
        description="Contact Jack's Pumps for emergency pump repairs in Dublin & Leinster. 24/7 emergency response across Dublin, Kildare, Meath, Wicklow, Wexford, Louth, and Carlow. Call +353 404 54753 or submit an online enquiry."
        canonical="/contact"
        keywords="contact pump repairs Dublin, emergency pump repairs Dublin, pump service Dublin, pump repairs Leinster, 24/7 pump repairs Dublin, emergency callout Dublin, pump station Dublin, pump repairs Kildare, pump repairs Meath, pump engineer Dublin"
        ogTitle="Contact Jack's Pumps | 24/7 Emergency Pump Repairs"
        ogDescription="24/7 emergency pump repair services across Ireland. Contact us for immediate assistance or service enquiries."
        schema={contactSchema}
      />
      
      <div className="min-h-screen bg-white">
        {/* Navigation */}
        <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-lg' : 'bg-white shadow-md'}`} role="navigation" aria-label="Main navigation">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-20 lg:h-24 py-2">
              <Link to="/" className="flex items-center -ml-2 sm:-ml-4 lg:ml-0" aria-label="Jack's Pumps Home">
                <img 
                  src="https://static.readdy.ai/image/dc999f92be6fe23ab0f2c081255e30e9/be41bd6c16e3f7ca48ba1bf859508677.jpeg" 
                  alt="Jack's Pumps Logo - Professional Pump Station Services Ireland" 
                  className="h-16 sm:h-20 lg:h-20 w-auto"
                  width="120"
                  height="80"
                />
              </Link>
              <div className="hidden md:flex items-center space-x-6 lg:space-x-8">
                <Link to="/" className="text-base lg:text-base font-medium text-gray-900 hover:text-[#00D084] transition-colors">Home</Link>
                <Link to="/services" className="text-base lg:text-base font-medium text-gray-900 hover:text-[#00D084] transition-colors">Services</Link>
                <Link to="/contact" className="text-base lg:text-base font-medium text-[#00D084] transition-colors">Contact</Link>
                <a href="tel:+35340454753" className="bg-[#00D084] text-white px-6 lg:px-8 py-3 lg:py-3.5 rounded-lg font-medium hover:bg-[#00B870] transition-colors whitespace-nowrap text-base" aria-label="Call Jack's Pumps for emergency pump repairs">
                  Call Now
                </a>
              </div>
              <div className="md:hidden">
                <a href="tel:+35340454753" className="bg-[#00D084] text-white px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap" aria-label="Call Jack's Pumps">
                  Call
                </a>
              </div>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <header className="relative pt-32 lg:pt-40 pb-20 lg:pb-28 bg-[#1A1A1A]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-block bg-[#00D084]/20 border border-[#00D084] px-4 lg:px-6 py-2 lg:py-3 rounded-full mb-6">
              <span className="text-[#00D084] text-sm lg:text-base font-medium uppercase tracking-wide">24/7 Emergency Response</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold text-white mb-6 lg:mb-8 leading-tight">
              <strong>Contact Jack's Pumps for Emergency Pump Repairs</strong>
            </h1>
            
            <p className="text-base sm:text-lg lg:text-xl xl:text-2xl text-gray-300 mb-10 max-w-4xl mx-auto">
              <strong>Emergency callouts</strong>, <strong>service enquiries</strong>, or <strong>quote requests</strong> - we're here to help across <strong>Dublin, Wicklow, Wexford, Kildare, Meath, Louth, and Carlow</strong>
            </p>
          </div>
        </header>

        {/* Contact Section */}
        <main>
          <section className="py-20 lg:py-28 bg-white" aria-labelledby="contact-methods-heading">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">
                {/* Left Column - Contact Methods */}
                <div className="lg:col-span-2">
                  <h2 id="contact-methods-heading" className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1A1A1A] mb-8">Contact Methods</h2>
                  
                  <div className="space-y-4 mb-8">
                    <a href="tel:+35340454753" className="w-full block bg-[#00D084] text-white p-6 rounded-xl hover:bg-[#00B870] transition-all" aria-label="Call Jack's Pumps at +353 404 54753">
                      <div className="flex items-center gap-4">
                        <i className="ri-phone-fill text-3xl" aria-hidden="true"></i>
                        <div className="text-left">
                          <p className="text-sm opacity-90">Tap to call</p>
                          <strong className="text-2xl font-bold">+353 404 54753</strong>
                        </div>
                      </div>
                    </a>

                    <a href="https://wa.me/35340454753" className="block bg-[#25D366] text-white p-6 rounded-xl hover:bg-[#20BA5A] transition-all" rel="noopener noreferrer" aria-label="Message Jack's Pumps on WhatsApp">
                      <div className="flex items-center gap-4">
                        <i className="ri-whatsapp-fill text-3xl" aria-hidden="true"></i>
                        <div>
                          <p className="text-sm opacity-90">Message us directly</p>
                          <strong className="text-xl font-bold">WhatsApp</strong>
                        </div>
                      </div>
                    </a>

                    <a href="mailto:info@jackspumps.ie" className="block bg-[#1A1A1A] text-white p-6 rounded-xl hover:bg-[#2A2A2A] transition-all" aria-label="Email Jack's Pumps at info@jackspumps.ie">
                      <div className="flex items-center gap-4">
                        <i className="ri-mail-fill text-3xl" aria-hidden="true"></i>
                        <div>
                          <p className="text-sm opacity-90">Email us</p>
                          <strong className="text-lg font-bold">info@jackspumps.ie</strong>
                        </div>
                      </div>
                    </a>
                  </div>

                  <div className="p-4 bg-red-50 border-l-4 border-red-500 rounded mb-8">
                    <p className="text-sm text-red-800 font-medium">
                      <i className="ri-error-warning-fill mr-2" aria-hidden="true"></i>
                      <strong>Emergency?</strong> Call immediately or submit form - we prioritise urgent issues
                    </p>
                  </div>

                  {/* Business Hours */}
                  <article className="bg-[#F5F5F5] p-6 rounded-xl">
                    <h3 className="text-xl font-bold text-[#1A1A1A] mb-4">Emergency Response</h3>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <i className="ri-time-line text-[#00D084] text-2xl" aria-hidden="true"></i>
                        <div>
                          <p className="font-semibold text-[#1A1A1A]">24/7 Availability</p>
                          <p className="text-sm text-gray-600">Emergency callouts anytime</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <i className="ri-map-pin-line text-[#00D084] text-2xl" aria-hidden="true"></i>
                        <div>
                          <p className="font-semibold text-[#1A1A1A]">Nationwide Coverage</p>
                          <p className="text-sm text-gray-600">All of Ireland</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <i className="ri-customer-service-2-line text-[#00D084] text-2xl" aria-hidden="true"></i>
                        <div>
                          <p className="font-semibold text-[#1A1A1A]">Always Captured</p>
                          <p className="text-sm text-gray-600">All enquiries logged 24/7</p>
                        </div>
                      </div>
                    </div>
                  </article>
                </div>

                {/* Right Column - Contact Form */}
                <div className="lg:col-span-3">
                  <EmergencyContactForm />
                </div>
              </div>
            </div>
          </section>

          {/* Service Quote Form */}
          <section className="py-20 lg:py-28 bg-[#F5F5F5]" aria-labelledby="quote-form-heading">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 id="quote-form-heading" className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1A1A1A] mb-4">Request a Service Quote</h2>
                <p className="text-base lg:text-lg text-gray-600">Tell us about your <strong>pump service requirements</strong> and we'll provide a detailed quote</p>
              </div>
              
              <ServiceQuoteForm />
            </div>
          </section>

          {/* Map Section */}
          <section className="py-20 lg:py-28 bg-white" aria-labelledby="service-area-heading">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 id="service-area-heading" className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1A1A1A] mb-4">Our Service Area</h2>
                <p className="text-base lg:text-lg text-gray-600">Covering <strong>Dublin, Wicklow, Wexford, Kildare, Meath, Louth, Carlow</strong> and all of <strong>Ireland</strong></p>
              </div>

              <div className="rounded-2xl overflow-hidden shadow-2xl h-96 lg:h-[500px]">
                <iframe 
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1222245.9443888!2d-8.5!3d53.5!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNTPCsDMwJzAwLjAiTiA4wrAzMCcwMC4wIlc!5e0!3m2!1sen!2sie!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Jack's Pumps Service Area Map - Ireland"
                  aria-label="Map showing Jack's Pumps service coverage across Ireland"
                ></iframe>
              </div>
            </div>
          </section>
        </main>

        {/* Footer */}
        <footer className="bg-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
              <div>
                <img 
                  src="https://static.readdy.ai/image/dc999f92be6fe23ab0f2c081255e30e9/be41bd6c16e3f7ca48ba1bf859508677.jpeg" 
                  alt="Jack's Pumps Logo - Professional Pump Station Services Ireland" 
                  className="h-40 w-auto mb-4 bg-white rounded-lg p-2"
                  width="160"
                  height="160"
                  loading="lazy"
                />
                <p className="text-[#00D084] text-sm mb-3">Pump Station Experts • Nationwide Coverage</p>
                <p className="text-gray-400 text-sm leading-relaxed">
                  Professional <strong>pump station servicing</strong>, <strong>repairs</strong>, and <strong>installations across Ireland</strong>.
                </p>
              </div>

              <nav aria-label="Footer navigation">
                <h3 className="text-white text-lg font-bold mb-4">Quick Links</h3>
                <div className="space-y-3">
                  <Link to="/" className="block text-gray-400 hover:text-[#00D084] transition-colors">Home</Link>
                  <Link to="/services" className="block text-gray-400 hover:text-[#00D084] transition-colors">Services</Link>
                  <Link to="/contact" className="block text-gray-400 hover:text-[#00D084] transition-colors">Contact</Link>
                </div>
              </nav>

              <div>
                <h3 className="text-white text-lg font-bold mb-4">Contact</h3>
                <a href="tel:+35340454753" className="block text-white text-xl font-semibold mb-2 hover:text-[#00D084] transition-colors" aria-label="Call Jack's Pumps">
                  <strong>+353 404 54753</strong>
                </a>
                <a href="mailto:info@jackspumps.ie" className="block text-gray-400 mb-6 hover:text-[#00D084] transition-colors" aria-label="Email Jack's Pumps">
                  info@jackspumps.ie
                </a>
                
                <h4 className="text-white font-semibold mb-2">Service Area</h4>
                <p className="text-gray-400 text-sm"><strong>Nationwide Coverage</strong></p>
              </div>
            </div>

            <div className="border-t border-[#00D084] pt-8">
              <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                <div className="flex gap-4">
                  <a href="#" className="text-white hover:text-[#00D084] transition-colors" aria-label="Follow Jack's Pumps on LinkedIn" rel="nofollow">
                    <i className="ri-linkedin-fill text-2xl" aria-hidden="true"></i>
                  </a>
                  <a href="#" className="text-white hover:text-[#00D084] transition-colors" aria-label="Follow Jack's Pumps on Facebook" rel="nofollow">
                    <i className="ri-facebook-fill text-2xl" aria-hidden="true"></i>
                  </a>
                </div>
                
                <p className="text-gray-400 text-sm">© 2025 Jack's Pumps. All rights reserved.</p>
                
                <div className="flex gap-4 text-sm">
                  <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer nofollow" className="text-gray-400 hover:text-[#00D084] transition-colors whitespace-nowrap">
                    Website Builder
                  </a>
                </div>
              </div>
            </div>
          </div>
        </footer>

        {/* Sticky Mobile Contact Bar */}
        <div className="fixed bottom-0 left-0 right-0 bg-[#1A1A1A] border-t-2 border-[#00D084] shadow-2xl z-50 lg:hidden" role="navigation" aria-label="Mobile contact options">
          <div className="grid grid-cols-3 h-18">
            <a href="tel:+35340454753" className="flex flex-col items-center justify-center py-3 hover:bg-[#2A2A2A] transition-colors" aria-label="Call Jack's Pumps">
              <i className="ri-phone-fill text-2xl text-[#00D084] mb-1" aria-hidden="true"></i>
              <span className="text-white text-xs font-medium">Call</span>
            </a>
            <a href="https://wa.me/35340454753" className="flex flex-col items-center justify-center py-3 hover:bg-[#2A2A2A] transition-colors border-x border-gray-700" rel="noopener noreferrer" aria-label="Message Jack's Pumps on WhatsApp">
              <i className="ri-whatsapp-fill text-2xl text-[#00D084] mb-1" aria-hidden="true"></i>
              <span className="text-white text-xs font-medium">Message</span>
            </a>
            <a href="#emergency-contact-form" className="flex flex-col items-center justify-center py-3 hover:bg-[#2A2A2A] transition-colors" aria-label="Request a quote">
              <i className="ri-file-list-3-line text-2xl text-[#00D084] mb-1" aria-hidden="true"></i>
              <span className="text-white text-xs font-medium">Quote</span>
            </a>
          </div>
        </div>
      </div>
    </>
  );
}

function EmergencyContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    location: '',
    issueType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('access_key', 'd8a90d32-fad8-4c33-b2f6-b27dc380d5fc');
      formBody.append('name', formData.name);
      formBody.append('mobile', formData.mobile);
      formBody.append('location', formData.location);
      formBody.append('issueType', formData.issueType);
      formBody.append('message', formData.message);
      formBody.append('subject', 'Emergency Contact Form - Jack\'s Pumps');

      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', mobile: '', location: '', issueType: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form id="emergency-contact-form" data-readdy-form onSubmit={handleSubmit} className="bg-[#F8F8F8] p-8 rounded-xl">
      <h3 className="text-2xl font-bold text-[#1A1A1A] mb-6">Emergency & General Enquiries</h3>
      
      <div className="space-y-4">
        <div>
          <label htmlFor="contact-name" className="block text-sm font-medium text-gray-700 mb-2">Your Name *</label>
          <input
            type="text"
            id="contact-name"
            name="name"
            placeholder="John Smith"
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full px-4 py-4 bg-white border border-gray-300 rounded-lg focus:outline-none focus:border-[#00D084] text-sm"
            aria-required="true"
          />
        </div>

        <div>
          <label htmlFor="contact-mobile" className="block text-sm font-medium text-gray-700 mb-2">Mobile Number *</label>
          <input
            type="tel"
            id="contact-mobile"
            name="mobile"
            placeholder="+353 87 123 4567"
            required
            value={formData.mobile}
            onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
            className="w-full px-4 py-4 bg-white border border-gray-300 rounded-lg focus:outline-none focus:border-[#00D084] text-sm"
            aria-required="true"
          />
        </div>

        <div>
          <label htmlFor="contact-location" className="block text-sm font-medium text-gray-700 mb-2">Location *</label>
          <input
            type="text"
            id="contact-location"
            name="location"
            placeholder="Dublin, Wicklow, etc."
            required
            value={formData.location}
            onChange={(e) => setFormData({ ...formData, location: e.target.value })}
            className="w-full px-4 py-4 bg-white border border-gray-300 rounded-lg focus:outline-none focus:border-[#00D084] text-sm"
            aria-required="true"
          />
        </div>

        <div>
          <label htmlFor="contact-issue-type" className="block text-sm font-medium text-gray-700 mb-2">Issue Type *</label>
          <select
            id="contact-issue-type"
            name="issueType"
            required
            value={formData.issueType}
            onChange={(e) => setFormData({ ...formData, issueType: e.target.value })}
            className="w-full px-4 py-4 bg-white border border-gray-300 rounded-lg focus:outline-none focus:border-[#00D084] text-sm"
            aria-required="true"
          >
            <option value="">Select Issue Type</option>
            <option value="Emergency" className="text-red-600 font-semibold">🚨 Emergency</option>
            <option value="Service">Service</option>
            <option value="Quote">Quote</option>
          </select>
        </div>

        <div>
          <label htmlFor="contact-message" className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
          <textarea
            id="contact-message"
            name="message"
            placeholder="Brief description of your issue"
            rows={4}
            maxLength={500}
            required
            value={formData.message}
            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            className="w-full px-4 py-4 bg-white border border-gray-300 rounded-lg focus:outline-none focus:border-[#00D084] resize-none text-sm"
            aria-required="true"
          ></textarea>
          <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-[#00D084] text-white py-4 rounded-lg font-bold text-lg hover:bg-[#00B870] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          aria-label="Send enquiry to Jack's Pumps"
        >
          {isSubmitting ? 'Sending...' : 'Send Enquiry'}
        </button>

        {submitStatus === 'success' && (
          <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm" role="alert">
            <i className="ri-checkbox-circle-fill mr-2" aria-hidden="true"></i>
            Thank you! We'll be in touch shortly.
          </div>
        )}

        {submitStatus === 'error' && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm" role="alert">
            <i className="ri-error-warning-fill mr-2" aria-hidden="true"></i>
            Something went wrong. Please try calling us directly.
          </div>
        )}

        <p className="text-xs text-gray-500 text-center">All enquiries captured 24/7</p>
      </div>
    </form>
  );
}

function ServiceQuoteForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    serviceType: '',
    propertyType: '',
    details: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('access_key', 'd8a90d32-fad8-4c33-b2f6-b27dc380d5fc');
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('company', formData.company);
      formBody.append('serviceType', formData.serviceType);
      formBody.append('propertyType', formData.propertyType);
      formBody.append('details', formData.details);
      formBody.append('subject', 'Service Quote Request - Jack\'s Pumps');

      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', company: '', serviceType: '', propertyType: '', details: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form id="service-quote-form" data-readdy-form onSubmit={handleSubmit} className="bg-white p-8 rounded-xl shadow-lg border-2 border-gray-200">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="quote-name" className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
          <input
            type="text"
            id="quote-name"
            name="name"
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:border-[#00D084] text-sm"
            aria-required="true"
          />
        </div>

        <div>
          <label htmlFor="quote-email" className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
          <input
            type="email"
            id="quote-email"
            name="email"
            required
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:border-[#00D084] text-sm"
            aria-required="true"
          />
        </div>

        <div>
          <label htmlFor="quote-phone" className="block text-sm font-medium text-gray-700 mb-2">Phone *</label>
          <input
            type="tel"
            id="quote-phone"
            name="phone"
            required
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:border-[#00D084] text-sm"
            aria-required="true"
          />
        </div>

        <div>
          <label htmlFor="quote-company" className="block text-sm font-medium text-gray-700 mb-2">Company (Optional)</label>
          <input
            type="text"
            id="quote-company"
            name="company"
            value={formData.company}
            onChange={(e) => setFormData({ ...formData, company: e.target.value })}
            className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:border-[#00D084] text-sm"
          />
        </div>

        <div>
          <label htmlFor="quote-service-type" className="block text-sm font-medium text-gray-700 mb-2">Service Required *</label>
          <select
            id="quote-service-type"
            name="serviceType"
            required
            value={formData.serviceType}
            onChange={(e) => setFormData({ ...formData, serviceType: e.target.value })}
            className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:border-[#00D084] text-sm"
            aria-required="true"
          >
            <option value="">Select Service</option>
            <option value="Servicing & Maintenance">Servicing & Maintenance</option>
            <option value="Pump Installation">Pump Installation</option>
            <option value="Repairs">Repairs</option>
            <option value="Diagnostics">Diagnostics</option>
            <option value="De-ragging & Cleaning">De-ragging & Cleaning</option>
            <option value="Service Contract">Service Contract</option>
            <option value="Other">Other</option>
          </select>
        </div>

        <div>
          <label htmlFor="quote-property-type" className="block text-sm font-medium text-gray-700 mb-2">Property Type *</label>
          <select
            id="quote-property-type"
            name="propertyType"
            required
            value={formData.propertyType}
            onChange={(e) => setFormData({ ...formData, propertyType: e.target.value })}
            className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:border-[#00D084] text-sm"
            aria-required="true"
          >
            <option value="">Select Type</option>
            <option value="Commercial">Commercial</option>
            <option value="Industrial">Industrial</option>
            <option value="Housing Estate">Housing Estate</option>
            <option value="Property Management">Property Management</option>
            <option value="Domestic">Domestic</option>
          </select>
        </div>
      </div>

      <div className="mt-6">
        <label htmlFor="quote-details" className="block text-sm font-medium text-gray-700 mb-2">Project Details *</label>
        <textarea
          id="quote-details"
          name="details"
          placeholder="Tell us about your requirements"
          rows={5}
          maxLength={500}
          required
          value={formData.details}
          onChange={(e) => setFormData({ ...formData, details: e.target.value })}
          className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:border-[#00D084] resize-none text-sm"
          aria-required="true"
        ></textarea>
        <p className="text-xs text-gray-500 mt-1">{formData.details.length}/500 characters</p>
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full mt-6 bg-[#00D084] text-white py-4 rounded-lg font-bold text-lg hover:bg-[#00B870] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
        aria-label="Request service quote from Jack's Pumps"
      >
        {isSubmitting ? 'Sending...' : 'Request Quote'}
      </button>

      {submitStatus === 'success' && (
        <div className="mt-4 bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm" role="alert">
          <i className="ri-checkbox-circle-fill mr-2" aria-hidden="true"></i>
          Quote request received! We'll respond within 24 hours.
        </div>
      )}

      {submitStatus === 'error' && (
        <div className="mt-4 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm" role="alert">
          <i className="ri-error-warning-fill mr-2" aria-hidden="true"></i>
          Something went wrong. Please try calling us directly.
        </div>
      )}
    </form>
  );
}
